import React, { useState } from 'react';
import { Box, Typography, Stack, Chip, Accordion, AccordionSummary, AccordionDetails, Paper } from '@mui/material';

// Icons
import StorageIcon from '@mui/icons-material/Storage';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TimelineIcon from '@mui/icons-material/Timeline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

export const TransactionLifecycleGraph = () => {
    const [expanded, setExpanded] = useState(false);

    return (
        <Accordion 
            expanded={expanded} 
            onChange={() => setExpanded(!expanded)} 
            sx={{ mb: 4, boxShadow: 'none', border: '1px solid #e2e8f0', borderRadius: '16px !important', '&:before': { display: 'none' } }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}>
                <Typography variant="caption" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                    <TimelineIcon fontSize="small" color="primary" sx={{ fontSize: 18 }} /> 
                    TRANSACTION LIFECYCLE (PIPELINE VIEW)
                </Typography>
            </AccordionSummary>
            
            <AccordionDetails sx={{ bgcolor: '#f8fafc', p: 4, borderTop: '1px solid #e2e8f0', borderBottomLeftRadius: 16, borderBottomRightRadius: 16, overflowX: 'auto' }}>
                <Box sx={{ minWidth: 1400 }}>
                    
                    {/* ASYNCHRONOUS FLOW (REFILL) */}
                    <Box sx={{ mb: 4, p: 2, border: '2px dashed #cbd5e1', borderRadius: 3, bgcolor: '#fff', width: 'fit-content' }}>
                        <Stack direction="row" alignItems="center" spacing={3}>
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <SyncAltIcon fontSize="small" /> ASYNCHRONOUS FLOW
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>Escrow Refill (Pay-in)</Typography>
                            </Box>
                            
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Chip label="Bank Account" size="small" icon={<AccountBalanceIcon />} sx={{ bgcolor: '#f1f5f9', fontWeight: 600 }} />
                                <ArrowForwardIcon sx={{ color: '#94a3b8', fontSize: 16 }} />
                                <Chip label="Escrow Wallet" size="small" icon={<AccountBalanceWalletIcon />} sx={{ bgcolor: '#f1f5f9', fontWeight: 600 }} />
                            </Stack>
                            
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', maxWidth: 200, ml: 2 }}>
                                Runs independently. Does not block the main synchronous pipeline.
                            </Typography>
                        </Stack>
                    </Box>

                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', mb: 2, display: 'block' }}>
                        SYNCHRONOUS PIPELINE (Strict Order)
                    </Typography>

                    {/* SYNCHRONOUS FLOW (PIPELINE) */}
                    <Stack direction="row" alignItems="stretch" spacing={1}>
                        
                        {/* STEP 1 */}
                        <FlowNode 
                            step="1" title="Reconciliation" icon={<StorageIcon />} color="#3b82f6" bgcolor="#eff6ff"
                            subtitle="Mapper & OnePay matched"
                        >
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Trigger matching on:</Typography>
                                <TypeChip label="Amount" />
                                <TypeChip label="SKU" />
                                <TypeChip label="Order ID" />
                                <TypeChip label="Producer ID" />
                            </Box>
                        </FlowNode>

                        <FlowConnector />

                        {/* STEP 2 */}
                        <FlowNode 
                            step="2" title="Transfer to Producer" icon={<AccountBalanceWalletIcon />} color="#f59e0b" bgcolor="#fffbeb"
                            subtitle="Escrow ➔ Producer Wallet"
                        >
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Transaction Types:</Typography>
                                <TypeChip label="Payment" />
                                <TypeChip label="Refund COD" />
                                <TypeChip label="Credit Note" />
                            </Box>
                        </FlowNode>

                        <FlowConnector />

                        {/* STEP 3 */}
                        <FlowNode 
                            step="3" title="Transfer to SC" icon={<AccountBalanceWalletIcon />} color="#f59e0b" bgcolor="#fffbeb"
                            subtitle="Target: Operator SC Wallet"
                        >
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>From Escrow (Requires Step 2):</Typography>
                                <TypeChip label="Payment" />
                                
                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5} mt={1.5}>From Producer Wallet:</Typography>
                                <TypeChip label="Refund" />
                                <TypeChip label="Fee" />
                                <TypeChip label="Payment COD" />
                            </Box>
                        </FlowNode>

                        <FlowConnector label="1st, 11th, 21st" />

                        {/* STEP 4 */}
                        <FlowNode 
                            step="4" title="Invoice Association" icon={<ReceiptLongIcon />} color="#8b5cf6" bgcolor="#f5f3ff"
                            subtitle="Billing cycle grouping"
                        >
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Mandatory Condition:</Typography>
                                <Box sx={{ bgcolor: '#ede9fe', p: 1, borderRadius: 1, border: '1px solid #ddd6fe' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#5b21b6', display: 'block', lineHeight: 1.3 }}>
                                        Transactions MUST be linked to a generated invoice to proceed.
                                    </Typography>
                                </Box>
                            </Box>
                        </FlowNode>

                        <FlowConnector label="Ready for Payout" />

                        {/* STEP 5 */}
                        <FlowNode 
                            step="5" title="Producer Payout" icon={<AccountBalanceIcon />} color="#10b981" bgcolor="#ecfdf5"
                            subtitle="To Producer Bank Account"
                        >
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Prerequisites:</Typography>
                                <Box sx={{ bgcolor: '#d1fae5', p: 1, borderRadius: 1, border: '1px solid #a7f3d0' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#065f46', display: 'block', lineHeight: 1.3 }}>
                                        All internal transfers completed AND attached to an Invoice.
                                    </Typography>
                                </Box>
                            </Box>
                        </FlowNode>

                        <FlowConnector />

                        {/* STEP 6 */}
                        <FlowNode 
                            step="6" title="Operator Payout" icon={<AccountBalanceIcon />} color="#10b981" bgcolor="#ecfdf5"
                            subtitle="To Operator Bank (SC)"
                        >
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Prerequisites:</Typography>
                                <Box sx={{ bgcolor: '#d1fae5', p: 1, borderRadius: 1, border: '1px solid #a7f3d0' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#065f46', display: 'block' }}>
                                        Step 5 (Producer Payout) strictly completed successfully.
                                    </Typography>
                                </Box>
                            </Box>
                        </FlowNode>

                    </Stack>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

// --- SOUS-COMPOSANTS UI ---

type FlowNodeProps = {
    step: string;
    title: string;
    subtitle: string;
    icon: React.ReactNode;
    color: string;
    bgcolor: string;
    children: React.ReactNode;
};

const FlowNode = ({ step, title, subtitle, icon, color, bgcolor, children }: FlowNodeProps) => (
    <Paper 
        elevation={0} 
        sx={{ 
            flex: 1, 
            p: 2, 
            borderRadius: 3, 
            border: `2px solid ${color}`, 
            bgcolor: bgcolor, 
            position: 'relative',
            display: 'flex',          
            flexDirection: 'column'   
        }}
    >
        <Box sx={{ position: 'absolute', top: -12, left: 16, bgcolor: color, color: '#fff', width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.75rem' }}>
            {step}
        </Box>
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5} mt={1}>
            <Box sx={{ color: color, display: 'flex' }}>{icon}</Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                {title}
            </Typography>
        </Stack>
        <Typography variant="caption" sx={{ color: color, fontWeight: 700, display: 'block', mb: 1, lineHeight: 1 }}>
            {subtitle}
        </Typography>
        <Box 
            sx={{ 
                bgcolor: '#fff', 
                p: 1.5, 
                borderRadius: 2, 
                border: '1px solid #e2e8f0', 
                flexGrow: 1                
            }}
        >
            {children}
        </Box>
    </Paper>
);
const FlowConnector = ({ label }: { label?: string }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 1, minWidth: 60 }}>
        {label && (
            <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', mb: 0.5, textAlign: 'center', fontSize: '0.6rem' }}>
                {label}
            </Typography>
        )}
        <ArrowForwardIcon sx={{ color: '#cbd5e1', fontSize: 32 }} />
    </Box>
);

const TypeChip = ({ label }: { label: string }) => (
    <Chip 
        label={label} 
        size="small" 
        sx={{ 
            bgcolor: '#f8fafc', 
            border: '1px solid #cbd5e1', 
            color: '#475569', 
            fontWeight: 700, 
            fontSize: '0.65rem',
            height: 20,
            m: 0.25
        }} 
    />
);